import React from 'react'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button } from '@material-ui/core'
import { GetServerSideProps } from 'next'
import Image from 'next/image'
import { getWallets } from '../src/services/wallets'
import { LoadState } from '../types/general'
import { Wallet as IWallet, WalletType } from '../types/wallet'
import WalletGrid from '../src/components/Product/wallets/WalletGrid'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import WalletGuide from '../src/components/Product/wallets/WalletGuide'
import FAQSection from '../src/components/Product/FAQSection'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 0',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',
    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
  listWrapper: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '1020px',
      margin: 'auto',
    },
  },
}))

const faqs = [
  {
    question: 'How do crypto wallets make money?',
    answer:
      'The business models of custodial wallet providers differ. Most make money when you trade one crypto asset for another or buy a crypto asset using fiat currency. Many also offer add-on services like crypto cards allowing you to spend your crypto in stores or to lend your crypto assets and earn interest on it.',
  },
  {
    question: 'I live in country X, can I get a custodial wallet?',
    answer: `Custodial wallets offer their services in a limited number of countries. Because custodial wallets hold your funds in custody and handle fiat payments they are subjected to regulation (unlike [non-custodial wallets](${urls.nonCustodialWallets})). This means they can only operate in whichever country they hold licenses. Search for a wallet that operates in your country.`,
  },
  {
    question: 'How do I choose a good custodial wallet?',
    answer:
      'When it comes down to choosing a good custodial wallet look at two things: a) reputation b) fees. Good reputation matters because it means the company has a track record of doing things right, no hacks, good customer support etc. Fees matter as well.',
  },
]

interface Props {
  wallets: IWallet[]
  initialLoadState: LoadState
}

const Wallets = ({ wallets }: Props): JSX.Element => {
  const classes = useStyles({})
  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters.com - Wallets`}
            description={`Cryptotesters.com.`}
            openGraph={{
              url: urls.custodialWallets,
              title: `Cryptotesters.com - Wallets`,
              description: `Custodial Wallets Cryptotesters.com.`,
              images: [
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/large/home.jpg`,
                  width: 1200,
                  height: 630,
                  alt: `Cryptotesters.com`,
                },
                {
                  url: `https://${process.env.NEXT_PUBLIC_APP_URL}/og/pages/small/home.jpg`,
                  width: 600,
                  height: 400,
                  alt: `Cryptotesters.com Logo`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">The best custodial crypto wallets</Typography>
                  <Typography variant="body2">
                    A custodial crypto wallet let’s you hold, send, receive and in some cases also
                    invest your crypto assets. However, you are not in full control over your funds.
                    Instead, you trust the wallet provider to “custody” your funds for you.
                    Custodial wallets are convenient and good for beginners.
                  </Typography>
                  <Button
                    color="primary"
                    variant="contained"
                    href="#wallets"
                    className={classes.heroCTA}
                  >
                    Learn more about wallets
                  </Button>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <Image src="/vault.png" objectFit="contain" layout="fill" />
                    </div>
                  </div>
                </Grid>
              </Grid>
              <div className={`${classes.section} ${classes.listWrapper}`} id="wallets">
                <WalletGrid wallets={wallets} type={WalletType.custodial} />
              </div>
            </div>

            <div>
              <WalletGuide />
              <FAQSection faqs={faqs} />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async () => {
  let loadState = LoadState.LOADING
  let wallets: IWallet[] = []

  try {
    wallets = await getWallets('custodial-wallets')
    loadState = LoadState.LOADED
  } catch (e) {
    loadState = LoadState.ERROR
  }

  return {
    props: {
      initialLoadState: loadState,
      wallets,
    },
  }
}

export default Wallets
